import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { Label, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import KeywordForm from 'talent_search/components/keyword_form';
import type { FilterType } from 'talent_search/shared/types';
import { CompanyMode } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { type ExperienceState } from 'talent_matching/components/filters/use_experience_state';

const t = withLocalePrefix('talent_matching.filter_row.experience');

// Seedling's Label is field-width (calc(100% - 2rem)); unset it or it strands the tooltip right.
const InlineLabel = styled(Label)`
  &.MuiFormLabel-root {
    width: auto;
  }
`;

type CompanySection = {
  mode: CompanyMode;
  // Borrowed from the keyword filter's chip input; only scopes its data-provides hooks.
  filterType: FilterType;
  label: string;
  description: string;
  booleanIndicator: string;
};

const COMPANY_SECTIONS: CompanySection[] = [
  {
    mode: 'can_have',
    filterType: 'can_have',
    label: t('can_have'),
    description: t('can_have_description'),
    booleanIndicator: t('or'),
  },
  {
    mode: 'include',
    filterType: 'must_have',
    label: t('must_have'),
    description: t('must_have_description'),
    booleanIndicator: t('and'),
  },
  {
    mode: 'exclude',
    filterType: 'does_not_have',
    label: t('does_not_have'),
    description: t('does_not_have_description'),
    booleanIndicator: t('and'),
  },
];

type ExperienceContentProps = {
  state: ExperienceState;
  // Discovery hides this: AdeptID filters on years of experience but has no
  // employer filter, so company sections there would narrow nothing.
  showCompanyExperience?: boolean;
};

export const ExperienceContent = ({
  state,
  showCompanyExperience = true,
}: ExperienceContentProps) => {
  const {
    companyValues,
    minValue,
    maxValue,
    setMinValue,
    setMaxValue,
    handleCompaniesChange,
    handleRemoveCompany,
  } = state;

  return (
    <Stack spacing={3} direction="column" sx={{ width: '100%' }}>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <SecondaryBody medium>{t('description')}</SecondaryBody>
        <Stack spacing={1} direction="row" sx={{ width: '100%' }}>
          <TextInput
            label={t('minimum')}
            value={minValue}
            onChange={(e) => setMinValue(e.target.value)}
            type="number"
            min={0}
            max={100}
          />
          <TextInput
            label={t('maximum')}
            value={maxValue}
            onChange={(e) => setMaxValue(e.target.value)}
            type="number"
            min={0}
            max={100}
          />
        </Stack>
      </Stack>

      {showCompanyExperience && (
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SecondaryBody medium>{t('company_experience')}</SecondaryBody>
          <Stack spacing={3} sx={{ width: '100%' }}>
            {COMPANY_SECTIONS.map((section) => (
              <Stack key={section.mode} spacing={1} sx={{ width: '100%' }}>
                <Stack spacing={1} direction="row" alignItems="center">
                  <InlineLabel>{section.label}</InlineLabel>
                  <HelpTooltip
                    label={section.description}
                    ariaLabel={section.description}
                  />
                </Stack>
                <KeywordForm
                  filterType={section.filterType}
                  values={companyValues[section.mode]}
                  onAdd={(values) =>
                    handleCompaniesChange(section.mode, values)
                  }
                  onRemove={(value) => handleRemoveCompany(section.mode, value)}
                  booleanIndicator={section.booleanIndicator}
                  pageName="search"
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
